import { LoadingOutlined, SendOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  DatePicker,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Radio,
  Select,
  Spin,
  Switch,
  Table,
} from 'antd';
import _ from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';

import ProjectCodesDropdown from '../../components/ProjectCodesDropdown';
import { fetchAPI } from '../../utils/api';
import { EntityForFilter } from './types';
import { OrderOperationStatus, OrderPaymentStatus } from './utils';

const OrderSendInvoice = () => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState<{
    q: string;
    page: number;
    open: boolean;
    selected?: EntityForFilter;
  }>({
    q: '',
    page: 1,
    open: false,
    selected: null,
  });
  const [startDate, setStartDate] = useState<string>(null);
  const [endDate, setEndDate] = useState<string>(null);
  const [operationStatuses, setOperationStatuses] = useState([]);
  const [paymentStatuses, setPaymentStatuses] = useState([]);
  const [projectCode, setProjectCode] = useState('');
  const [form] = Form.useForm<{
    entity_id: number;
    project_code: string;
    consolidate_po_number: string;
    saids: number[];
  }>();
  const [useBulkSAIDs, setUseBulkSAIDs] = useState(false);

  const entities = useQuery<{
    items: EntityForFilter[];
    page: number;
    per_page: number;
    total: number;
    loading: boolean;
  }>(
    ['entity/list-for-filter', filters, visible],
    async () => {
      if (!visible) {
        return {
          items: [],
          page: filters.page,
          per_page: 10,
          total: 0,
          loading: false,
        };
      }
      try {
        const resp = await fetchAPI('entity/list-for-filter', {
          method: 'POST',
          payload: {
            q: filters.q,
            page: filters.page,
            per_page: 10,
          },
        });

        return resp;
      } catch (error) {
        message.error('Failed get entities');
        return {
          items: [],
          page: filters.page,
          per_page: 10,
          total: 0,
          loading: false,
        };
      }
    },
    {
      staleTime: 10 * 60 * 1000,
      placeholderData: {
        items: [],
        page: filters.page,
        per_page: 10,
        total: 0,
        loading: true,
      },
    }
  );

  const calculateInvoiceTotal = async values => {
    try {
      setLoading(true);
      const payload = {
        ...values,
        saids: (values?.saids || []).map(said => Number(said)),
        from_date: startDate,
        to_date: endDate,
      };
      const resp = await fetchAPI(`order/current-calc`, {
        method: 'POST',
        payload,
      });

      const calcResult = [
        {
          label: 'Subtotal (Zero Rated)',
          amount: `${resp.subtotal_non_tax} ${resp.currency}`,
        },
        {
          label: 'Subtotal (Tax)',
          amount: `${resp.subtotal_taxed} ${resp.currency}`,
        },
        {
          label: 'Handling Fee',
          amount: `${resp.handling_fee} ${resp.currency}`,
        },
        {
          label: 'Taxes',
          amount: `${resp.tax} ${resp.currency}`,
        },
        {
          label: 'Invoice Total',
          amount: `${resp.total} ${resp.currency}`,
        },
      ];

      Modal.info({
        title: 'Total Invoice Calculation',
        content: (
          <Table
            size="small"
            dataSource={calcResult}
            columns={[
              {
                title: '',
                key: 'label',
                dataIndex: 'label',
              },
              {
                title: '',
                key: 'amount',
                dataIndex: 'amount',
              },
            ]}
            pagination={false}
            showHeader={false}
          />
        ),
        okText: 'Confirm send order invoice!',
        onOk: () => doSendOrderInvoicing(payload),
      });
      // message.success('Order invoicing email added to Queue!');
      // setVisible(false);
    } catch (error) {
      message.error('Failed calculate invoice total');
    }
    setLoading(false);
  };

  const doSendOrderInvoicing = async payload => {
    try {
      setLoading(true);
      // const payload = {
      //   ...values,
      //   saids: (values?.saids || []).map(said => Number(said)),
      //   from_date: startDate,
      //   to_date: endDate,
      // };
      await fetchAPI(`order/report/invoicing`, {
        method: 'POST',
        payload,
      });
      message.success('Order invoicing email added to Queue!');
      setVisible(false);
    } catch (error) {
      message.error('Failed send order invoicing');
    }
    setLoading(false);
  };

  const generateInvoiceNumber = async (entityId: number) => {
    try {
      const resp = await fetchAPI(`order/invoicing/entity/${entityId}/number`);
      form.setFieldsValue({ consolidate_po_number: resp?.latest_number });
    } catch (error) {
      message.error('Failed generate invoice number');
      if (error.message) {
        message.error(error.message);
      }
    }
  };

  const saids = useQuery(
    [
      'said-list-consolidate-invoice',
      visible,
      filters.selected?.id,
      startDate,
      endDate,
      operationStatuses,
      paymentStatuses,
      projectCode,
    ],
    async () => {
      if (!visible || !filters.selected?.id) {
        return {
          items: [],
          loading: false,
        };
      }

      const resp = await getInvoicingSAIDS({
        entity_id: filters.selected?.id,
        from_date: startDate,
        to_date: endDate,
        status: operationStatuses,
        payment_status: paymentStatuses,
        project_code: projectCode,
        // "q": "27"
      });

      return { items: resp };
    },
    {
      staleTime: 5 * 60 * 1000,
      placeholderData: {
        items: [],
        loading: true,
      },
    }
  );

  const entitiesLoading = entities.isLoading || entities.data.loading;

  return (
    <>
      <Button
        icon={<SendOutlined />}
        onClick={() => {
          setFilters({ q: '', page: 1, open: false, selected: null });
          form.resetFields();
          setVisible(true);
          setStartDate(null);
          setEndDate(null);
          setOperationStatuses([]);
          setPaymentStatuses([]);
          setProjectCode('');
        }}
      >
        Send Order Invoicing
      </Button>
      <Modal
        title="Send Order Invoicing"
        visible={visible}
        onCancel={() => !loading && setVisible(false)}
        onOk={() => form.submit()}
        cancelButtonProps={{
          disabled: loading,
        }}
        okText={'Calculate Invoice Total'}
        okButtonProps={{ loading }}
      >
        <Form
          form={form}
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
          initialValues={{
            is_show_detail: true,
            is_update_invoice_date: true,
          }}
          // onFinish={values => doSendOrderInvoicing(values)}
          onFinish={values => calculateInvoiceTotal(values)}
        >
          <Form.Item
            label="Entity"
            rules={[{ type: 'number', required: true }]}
            name="entity_id"
          >
            <Select
              placeholder="Select Entity"
              open={filters.open}
              onDropdownVisibleChange={vis =>
                setFilters(f => ({ ...f, open: vis }))
              }
              showSearch
              filterOption={false}
              onSearch={_.debounce(
                value => setFilters(f => ({ ...f, page: 1, q: value })),
                350
              )}
              disabled={loading}
              onSelect={value => {
                if (value === -1) {
                  setFilters(f => ({ ...f, page: f.page - 1 }));
                  setTimeout(() => {
                    setFilters(f => ({ ...f, open: true }));
                  }, 150);
                  return;
                }
                if (value === -2) {
                  setFilters(f => ({ ...f, page: f.page + 1 }));
                  setTimeout(() => {
                    setFilters(f => ({ ...f, open: true }));
                  }, 150);
                  return;
                }

                const selectedEntity = entities.data.items.find(
                  obj => obj.id === value
                );
                setFilters(f => ({ ...f, selected: selectedEntity }));
                generateInvoiceNumber(selectedEntity?.id);
              }}
              notFoundContent={
                entitiesLoading ? (
                  <Spin
                    indicator={<LoadingOutlined />}
                    style={{ width: '100%', padding: 15 }}
                  />
                ) : (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Entities not found :("
                  />
                )
              }
            >
              {!entitiesLoading && filters.page > 1 && (
                <Select.Option value={-1}>
                  <span
                    style={{ width: '100%', textAlign: 'left' }}
                  >{`<< Prev`}</span>
                </Select.Option>
              )}
              {entities.data.items.map((obj, idx) => {
                return (
                  <Select.Option key={idx} value={obj.id}>
                    {obj.name} [{obj.id}]
                  </Select.Option>
                );
              })}
              {!entitiesLoading &&
                filters.page * entities.data.per_page < entities.data.total && (
                  <Select.Option value={-2}>
                    <span
                      style={{ width: '100%', textAlign: 'right' }}
                    >{`Next >>`}</span>
                  </Select.Option>
                )}
            </Select>
          </Form.Item>
          <Form.Item label="Invoice Number" name="consolidate_po_number">
            <Input
              placeholder="Consolidate Invoice Number"
              disabled={!filters.selected?.id}
            />
          </Form.Item>
          <Form.Item
            label="Start Date"
            name="from_date"
            getValueFromEvent={val => {
              if (moment.isMoment(val)) {
                return val.format('YYYY-MM-DD');
              }

              return val;
            }}
            getValueProps={val => moment(val)}
          >
            <div>
              <Radio.Group
                disabled={loading}
                options={[
                  {
                    label: 'Start of this month',
                    value: moment()
                      .date(1)
                      .format('YYYY-MM-DD'),
                  },
                  {
                    label: '15th of this month',
                    value: moment()
                      .date(15)
                      .format('YYYY-MM-DD'),
                  },
                ]}
                optionType="button"
                buttonStyle="solid"
                style={{ marginBottom: 15 }}
                value={startDate}
                onChange={e => setStartDate(e.target?.value)}
              />
              <DatePicker
                format={'DD MMM YYYY'}
                placeholder="Select Start Date for the Order"
                value={!!startDate ? moment(startDate) : undefined}
                onChange={momDate =>
                  !!momDate
                    ? setStartDate(momDate.format('YYYY-MM-DD'))
                    : setStartDate(null)
                }
                disabled={loading}
              />
            </div>
          </Form.Item>
          <Form.Item
            label="End Date"
            name="to_date"
            getValueFromEvent={val => {
              if (moment.isMoment(val)) {
                return val.format('YYYY-MM-DD');
              }

              return val;
            }}
            getValueProps={val => moment(val)}
          >
            <div>
              <Radio.Group
                disabled={loading}
                options={[
                  {
                    label: '15th of this month',
                    value: moment()
                      .date(15)
                      .format('YYYY-MM-DD'),
                  },
                  {
                    label: 'End of this month',
                    value: moment()
                      .date(1)
                      .add(1, 'month')
                      .subtract(1, 'day')
                      .format('YYYY-MM-DD'),
                  },
                ]}
                optionType="button"
                buttonStyle="solid"
                style={{ marginBottom: 15 }}
                value={endDate}
                onChange={e => setEndDate(e.target?.value)}
              />
              <DatePicker
                format={'DD MMM YYYY'}
                placeholder="Select End Date for the Order"
                value={!!endDate ? moment(endDate) : undefined}
                onChange={momDate =>
                  !!momDate
                    ? setEndDate(momDate.format('YYYY-MM-DD'))
                    : setEndDate(null)
                }
                disabled={loading}
              />
            </div>
          </Form.Item>
          <Form.Item label={<span>Order Operation Status</span>} name="status">
            <Select
              placeholder="Select Order Operation Status"
              options={Object.keys(OrderOperationStatus).map(str => {
                return {
                  value: OrderOperationStatus[str],
                  label: OrderOperationStatus[str],
                };
              })}
              mode="tags"
              disabled={loading}
              onChange={values => {
                if (Array.isArray(values)) {
                  setOperationStatuses(values);
                }
              }}
            />
          </Form.Item>
          <Form.Item
            label={<span>Order Payment Status</span>}
            name="payment_status"
          >
            <Select
              placeholder="Select Order Payment Status"
              options={Object.keys(OrderPaymentStatus).map(str => {
                return {
                  value: OrderPaymentStatus[str],
                  label: OrderPaymentStatus[str],
                };
              })}
              mode="tags"
              disabled={loading}
              onChange={values => {
                if (Array.isArray(values)) {
                  setPaymentStatuses(values);
                }
              }}
            />
          </Form.Item>
          <Form.Item label={<span>Project Code</span>} name="project_code">
            <ProjectCodesDropdown
              visible={visible}
              disabled={loading || !filters?.selected?.id}
              onChange={pc => {
                form.setFieldsValue({
                  project_code: pc,
                });
                setProjectCode(pc);
              }}
              entityId={filters?.selected?.id}
              additionalFilters={{
                status: operationStatuses,
                payment_status: paymentStatuses,
              }}
            />
          </Form.Item>
          <Form.Item label="Use Bulk SAID(s)">
            <Switch
              size="small"
              checked={useBulkSAIDs}
              onChange={val => setUseBulkSAIDs(val)}
            />
          </Form.Item>
          <Form.Item label={<span>SAIDs</span>} name="saids">
            {!!useBulkSAIDs ? (
              <Select
                placeholder="Input SAID(s)"
                mode="tags"
                onChange={(selectValues: string[]) => {
                  const ids = selectValues.reduce((arr, strId) => {
                    if (!!Number(strId)) {
                      return [...arr, strId];
                    }

                    const idsAgain = strId.split(/[\s\n]+/);
                    const invalidIds = [];
                    const duplicatedIds = [];
                    const validIds = [...arr, ...idsAgain].filter(
                      (str, idx, arr) => {
                        // filter invalid and duplicated id
                        // invalid id
                        if (!Number(str)) {
                          invalidIds.push(str);
                          return false;
                        }

                        // duplicated id
                        const elIdx = arr.findIndex(arrStr => arrStr === str);
                        if (elIdx !== idx) {
                          duplicatedIds.push(str);
                          return false;
                        }

                        return true;
                      }
                    );

                    if (invalidIds.length) {
                      message.error(
                        `Invalid SAIDs: ${invalidIds.join(', ')}`,
                        3
                      );
                    }
                    if (duplicatedIds.length) {
                      message.error(
                        `Duplicated SAIDs: ${duplicatedIds.join(', ')}`,
                        3
                      );
                    }
                    return validIds;
                  }, []);
                  form.setFieldsValue({ saids: ids });
                }}
              />
            ) : (
              <Select
                placeholder="Select Multiple SAID"
                mode="multiple"
                loading={saids.data.loading || saids.isLoading}
                disabled={loading}
                filterOption={(inputValue, option) => {
                  if (!inputValue) {
                    return true;
                  }

                  const strLabel = option.str_label || '';
                  return strLabel
                    .toLowerCase()
                    .includes(inputValue.toLowerCase());
                }}
                options={(saids.data.items || []).map(obj => {
                  return {
                    value: String(obj.id),
                    label: (
                      <span>
                        <b>[{obj.id}]</b> {obj.ref_no}
                      </span>
                    ),
                    str_label: `${obj.id} ${obj.ref_no}`,
                  };
                })}
              />
            )}
          </Form.Item>
          <Form.Item
            name="is_notify_entity"
            valuePropName="checked"
            wrapperCol={{ span: 16, offset: 8 }}
          >
            <Checkbox disabled={loading}>Notify Buyer Entity</Checkbox>
          </Form.Item>
          <Form.Item
            label="Override Buyer Email"
            name="override_buyer_email"
            rules={[{ type: 'email', message: 'Invalid email format' }]}
          >
            <Input
              placeholder="(Optional) Input email to override the receiver"
              disabled={loading}
            />
          </Form.Item>
          <Form.Item
            name="is_show_detail"
            valuePropName="checked"
            wrapperCol={{ span: 16, offset: 8 }}
          >
            <Checkbox disabled={loading}>Use Detailed Template</Checkbox>
          </Form.Item>
          <Form.Item
            name="all_taxed"
            valuePropName="checked"
            wrapperCol={{ span: 16, offset: 8 }}
          >
            <Checkbox disabled={loading}>Force Use Tax</Checkbox>
          </Form.Item>
          <Form.Item
            name="is_replace_consolidate_number"
            valuePropName="checked"
            wrapperCol={{ span: 16, offset: 8 }}
          >
            <Checkbox disabled={loading}>
              Replace Existing Invoice Number
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="is_update_order_payment"
            valuePropName="checked"
            wrapperCol={{ span: 16, offset: 8 }}
          >
            <Checkbox disabled={loading}>
              Change the payment status from "Pending Invoice Submission" to
              "Pending Payment"
            </Checkbox>
          </Form.Item>
          <Form.Item
            name="is_update_invoice_date"
            valuePropName="checked"
            wrapperCol={{ span: 16, offset: 8 }}
          >
            <Checkbox disabled={loading}>
              Change Invoice Date according to "End Date"
            </Checkbox>
          </Form.Item>
          <button type="submit" style={{ display: 'none' }} />
        </Form>
      </Modal>
    </>
  );
};

const getInvoicingSAIDS = async (payload, page = 1, prevs = []) => {
  if (page > 10) {
    message.error(
      `SAIDs contain more than 200 data, some data will be hidden. Please change filter to get more specific results`
    );
    return prevs;
  }

  try {
    const resp = await fetchAPI('order/invoicing/saids', {
      method: 'POST',
      payload: {
        ...payload,
        page,
      },
    });

    const all = [...prevs, ...resp.items];
    if (resp.total > resp.page * resp.per_page) {
      return getInvoicingSAIDS(payload, page + 1, all);
    }

    return all;
  } catch (error) {
    message.error(`Failed get SAIDs on page: ${page}`);

    return prevs;
  }
};

export default OrderSendInvoice;
